import { useState, useEffect, useRef, useId } from 'react';
import { XMarkIcon, ArchiveBoxArrowDownIcon } from '@heroicons/react/24/outline';
import { Switch } from '@headlessui/react';
import { Link } from 'react-router-dom';
import { SearchableMultiSelect } from '@/components/ui/SearchableMultiSelect';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { CollectionTabs } from './CollectionTabs';
import type { CollectionWithTotal, Tag, LedgerType } from '@/api/types';

interface LedgerFiltersProps {
    collections: CollectionWithTotal[];
    tags: Tag[];
    selectedCollectionIds: number[];
    selectedTagIds: number[];
    showArchived: boolean;
    onCollectionChange: (collectionIds: number[]) => void;
    onCollectionReorder?: (collectionId: number, newWeight: number) => void;
    onTagsChange: (tagIds: number[]) => void;
    /** Called when tags dropdown closes - triggers deferred update */
    onTagsClose?: () => void;
    onShowArchivedChange: (showArchived: boolean) => void;
    hasArchivedLedgers: boolean;
    hasActiveFilters?: boolean;
    onReset?: () => void;
    /** Hide the archived toggle (used in analytics view) */
    hideArchivedToggle?: boolean;
    /** Ledger type for tags (asset or debt) */
    type?: LedgerType;
}

/**
 * Filter controls for the ledger list.
 * Two-row layout:
 * - Row 1: CollectionTabs (horizontal tab-style with totals)
 * - Row 2: Tags multi-select + Archive toggle
 */
export function LedgerFilters({
    collections,
    tags,
    selectedCollectionIds,
    selectedTagIds,
    showArchived,
    onCollectionChange,
    onCollectionReorder,
    onTagsChange,
    onTagsClose,
    onShowArchivedChange,
    hasArchivedLedgers,
    hasActiveFilters,
    onReset,
    hideArchivedToggle,
    type = 'asset',
}: LedgerFiltersProps) {
    const [tagsHelpOpen, setTagsHelpOpen] = useState(false);
    const archivedLabelId = useId();
    const tagOptions = tags.map((t) => ({ value: t.id, label: t.label }));
    const typeLabel = type === 'asset' ? 'asset' : 'debt';

    // Delay enabling reset button to prevent accidental clicks on mobile
    // when button becomes visible mid-touch interaction
    const [resetEnabled, setResetEnabled] = useState(false);
    const resetTimerRef = useRef<number | undefined>(undefined);

    useEffect(() => {
        if (hasActiveFilters) {
            resetTimerRef.current = window.setTimeout(() => {
                setResetEnabled(true);
            }, 150);
        } else {
            setResetEnabled(false);
            if (resetTimerRef.current) {
                clearTimeout(resetTimerRef.current);
            }
        }
        return () => {
            if (resetTimerRef.current) {
                clearTimeout(resetTimerRef.current);
            }
        };
    }, [hasActiveFilters]);

    return (
        <div className="space-y-4">
            {/* Row 1: Collection Tabs */}
            {collections.length > 0 && (
                <CollectionTabs
                    collections={collections}
                    selectedIds={selectedCollectionIds}
                    onSelectionChange={onCollectionChange}
                    onCollectionReorder={onCollectionReorder}
                />
            )}

            {/* Row 2: Archive Toggle + Tags + Reset */}
            <div className="flex items-center gap-2 sm:gap-4">
                {/* Show Archived Toggle - First if available and not hidden */}
                {hasArchivedLedgers && !hideArchivedToggle && (
                    <div className="group relative flex items-center gap-2 h-9 px-2 sm:px-3 border border-gray-300 dark:border-gray-700 bg-white dark:bg-secondary rounded-md shrink-0">
                        <span id={archivedLabelId} title="Archived" className="text-gray-500 dark:text-gray-400">
                            <ArchiveBoxArrowDownIcon className="h-5 w-5" aria-hidden="true" />
                            <span className="sr-only">Archived</span>
                        </span>
                        <Switch
                            checked={showArchived}
                            onChange={onShowArchivedChange}
                            aria-labelledby={archivedLabelId}
                            className={`${
                                showArchived ? 'bg-accent' : 'bg-gray-300 dark:bg-gray-600'
                            } relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2`}
                        >
                            <span
                                className={`${
                                    showArchived ? 'translate-x-4' : 'translate-x-0'
                                } pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out`}
                            />
                        </Switch>
                    </div>
                )}

                {/* Tags Filter - flexible width */}
                <div className="flex-1 min-w-0 sm:flex-none sm:w-64">
                    <SearchableMultiSelect
                        size="sm"
                        value={selectedTagIds}
                        onChange={onTagsChange}
                        onClose={onTagsClose}
                        options={tagOptions}
                        placeholder="Tags"
                        placeholderDesktop="Tags"
                        disabled={tagOptions.length === 0}
                        onDisabledClick={() => setTagsHelpOpen(true)}
                        compact
                    />
                </div>

                {/* Reset Filters Button - visible when active */}
                <button
                    type="button"
                    onClick={onReset}
                    title="Reset filters"
                    className={`h-9 flex items-center justify-center gap-1.5 px-2 sm:px-3 text-sm border rounded-md transition-colors shrink-0 ${
                        resetEnabled && onReset
                            ? 'text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-700 bg-white dark:bg-secondary hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                            : 'invisible pointer-events-none'
                    }`}
                    disabled={!resetEnabled}
                >
                    <XMarkIcon className="w-4 h-4" />
                    <span className="hidden sm:inline">Reset</span>
                </button>
            </div>

            {/* Tags Help Dialog */}
            <Dialog
                open={tagsHelpOpen}
                onClose={() => setTagsHelpOpen(false)}
                title="About Tags"
                size="sm"
            >
                <div className="space-y-4 text-sm text-gray-600 dark:text-gray-300">
                    <p>
                        Tags help you organize and filter your {typeLabel}s across collections.
                        Use them to group related items or track specific categories.
                    </p>
                    <div>
                        <p className="font-medium text-gray-900 dark:text-white mb-1">Example tags:</p>
                        <ul className="list-disc list-inside space-y-0.5 text-gray-500 dark:text-gray-400">
                            <li>Retirement</li>
                            <li>Emergency Fund</li>
                            <li>High Risk</li>
                            <li>Tax Advantaged</li>
                        </ul>
                    </div>
                    <p>
                        You can assign multiple tags to each {typeLabel}.
                    </p>
                    {tagOptions.length === 0 && (
                        <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                            <p className="text-gray-500 dark:text-gray-400 mb-3">
                                You haven't created any {typeLabel} tags yet.
                            </p>
                            <Link
                                to="/settings"
                                onClick={() => setTagsHelpOpen(false)}
                                className="inline-flex"
                            >
                                <Button variant="primary" size="sm">
                                    Go to Settings
                                </Button>
                            </Link>
                        </div>
                    )}
                </div>
            </Dialog>
        </div>
    );
}
